import React, { createContext, useContext, useState, useCallback } from 'react';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';

interface ConfirmOptions {
    title?: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
}

interface ConfirmContextType {
    confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<{
        message: string;
        title: string;
        confirmText: string;
        cancelText: string;
        isDangerous: boolean;
        resolve: (value: boolean) => void;
    } | null>(null);

    const confirm = useCallback((message: string, options?: ConfirmOptions) => {
        return new Promise<boolean>((resolve) => {
            setConfig({
                message,
                title: options?.title || 'Confirmation',
                confirmText: options?.confirmText || 'Confirmer',
                cancelText: options?.cancelText || 'Annuler',
                isDangerous: options?.isDangerous ?? true,
                resolve,
            });
            setIsOpen(true);
        });
    }, []);

    const handleClose = () => {
        setIsOpen(false);
        config?.resolve(false);
        setTimeout(() => setConfig(null), 300);
    };

    const handleConfirm = () => {
        setIsOpen(false);
        config?.resolve(true);
        setTimeout(() => setConfig(null), 300);
    };

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            {config && (
                <ConfirmationModal
                    isOpen={isOpen}
                    onClose={handleClose}
                    onConfirm={handleConfirm}
                    title={config.title}
                    message={config.message}
                    confirmText={config.confirmText}
                    cancelText={config.cancelText}
                    isDangerous={config.isDangerous}
                />
            )}
        </ConfirmContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context;
};
